import { useContext, useEffect, useRef, useState } from 'react';
import { ChatWidget } from './components/ChatWidget';
import { CdnChatWidget } from './components/CdnChatWidget';
import { isCdnWidgetEnabled } from './config/cdnWidget';
import { DashboardPage } from './components/DashboardPage';
import { LandingPage } from './components/LandingPage';
import { Header } from './components/Header';
import { SettingsPage } from './components/SettingsPage';
import { TeamsSupportPage } from './components/TeamsSupportPage';
import { RewardsSupportPage } from './components/RewardsSupportPage';
import { useAuth } from './auth/useAuth';
import { RuntimeConfigContext } from './config/runtimeConfigContext';
import { AdminPortal } from './admin/AdminPortal';
import { isContactPortalHost } from './config/contactPortal';
import { resolveRoute, TEAMS_CLEAN_PATH, type RouteDecision } from './config/portalRouting';
import { agentOptions, type AgentOption } from './config/agentOptions';
import { closeBrowserLiveOwnership } from './api/chatClient';

// Routing rules (contact portal hosts vs every other host, feature-flag gating, redirects) live in
// config/portalRouting.ts. The Rewards page takes its locale from the /<locale>/ path prefix.
const LOCALE_PREFIX_RE = /^\/([a-z]{2}-[a-z]{2})(?=\/|$)/i;
const DEFAULT_LOCALE = 'en-us';

function readRoute(flags: { contactPortalHomeEnabled: boolean; teamsEnabled: boolean; rewardsEnabled: boolean }): RouteDecision {
  return resolveRoute({
    origin: window.location.origin,
    pathname: window.location.pathname,
    hash: window.location.hash,
    contactPortalHost: isContactPortalHost(),
    flags,
  });
}

/** Locale from a /<locale>/ path prefix (e.g. "en-us" in /en-us/#/rewards or /en-us/rewards); defaults to en-us. */
function readRewardsLocale(): string {
  return (window.location.pathname.match(LOCALE_PREFIX_RE)?.[1] ?? DEFAULT_LOCALE).toLowerCase();
}

// Persist the selected agent for the lifetime of the browser tab. Session (not local) storage is
// deliberate: the selection should survive a full-page navigation — notably the MSAL sign-in redirect,
// which reloads the SPA and would otherwise reset the selection to the default — but reset to the
// default once the user closes the window/tab.
const SELECTED_AGENT_KEY = 'cmsp.selectedAgentId';

function loadStoredAgent(): AgentOption | undefined {
  try {
    const id = window.sessionStorage.getItem(SELECTED_AGENT_KEY);
    return id ? agentOptions.find(a => a.id === id) : undefined;
  } catch {
    return undefined;
  }
}

function storeSelectedAgent(agent: AgentOption): void {
  try {
    window.sessionStorage.setItem(SELECTED_AGENT_KEY, agent.id);
  } catch {
    // Storage unavailable (private mode / disabled) — selection simply won't persist across reloads.
  }
}

/**
 * Resolve the initial agent. An explicit ?entry= deep link wins (fresh intent on this load), then a
 * previously stored selection (survives the sign-in redirect), and finally the default agent.
 */
function initialAgent(): AgentOption {
  const params = new URLSearchParams(window.location.search);
  const entry = params.get('entry');
  if (entry) {
    const fromEntry = agentOptions.find(a => a.entryPoint === entry);
    if (fromEntry) {
      // Persist the deep-linked selection immediately: MSAL signs in with redirectUri = window.location.origin,
      // so the ?entry= query is dropped on the sign-in roundtrip. Without storing it now, the post-login reload
      // would fall back to the previously stored (or default) agent instead of the deep-linked one.
      storeSelectedAgent(fromEntry);
      return fromEntry;
    }
    return agentOptions[0];
  }
  return loadStoredAgent() ?? agentOptions[0];
}

export function App() {
  // Runtime config is loaded (and provided) before the app renders in main.tsx, so the feature flag is
  // available on the very first render — the rewards route resolves without a flash of the wrong page.
  const cfg = useContext(RuntimeConfigContext);
  // Every routing flag fails closed if absent. See config/portalRouting.ts for the full rule set.
  const rewardsEnabled = cfg?.rewardsEnabled ?? false;
  const teamsEnabled = cfg?.teamsEnabled ?? false;
  const contactPortalHomeEnabled = cfg?.contactPortalHomeEnabled ?? false;
  const [decision, setDecision] = useState<RouteDecision>(
    () => readRoute({ contactPortalHomeEnabled, teamsEnabled, rewardsEnabled }));
  const [chatRequest, setChatRequest] = useState<{ action: 'open' } | { action: 'send'; message: string } | null>(null);
  const { isSignedIn, account, isAuthPending } = useAuth();
  const liveOwner = useRef(account?.homeAccountId);
  useEffect(() => {
    if (isAuthPending) return;
    if (liveOwner.current !== account?.homeAccountId) {
      void closeBrowserLiveOwnership().catch(error => console.warn('Live ownership teardown failed', error));
      liveOwner.current = account?.homeAccountId;
    }
  }, [account?.homeAccountId, isAuthPending]);
  const [selectedAgent, setSelectedAgent] = useState<AgentOption>(initialAgent);

  // Persist the selection so it survives a full-page reload (e.g. the MSAL sign-in redirect).
  const changeAgent = (agent: AgentOption) => {
    storeSelectedAgent(agent);
    setSelectedAgent(agent);
  };
  // Bring-up trial: when the CDN widget flag is on, ADDITIONALLY embed the
  // CDN-hosted generic widget ALONGSIDE the built-in chat (in its own dock) so
  // the two can be compared side by side. Resolved once per load (query param or
  // build flag); additive + default off, so the default portal is unchanged.
  const useCdnWidget = isCdnWidgetEnabled();

  useEffect(() => {
    // popstate covers clean-path navigation (path changes don't fire hashchange); hashchange covers the
    // #/rewards form and the app's other hash routes.
    const handler = () => setDecision(readRoute({ contactPortalHomeEnabled, teamsEnabled, rewardsEnabled }));
    window.addEventListener('hashchange', handler);
    window.addEventListener('popstate', handler);
    return () => {
      window.removeEventListener('hashchange', handler);
      window.removeEventListener('popstate', handler);
    };
  }, [contactPortalHomeEnabled, teamsEnabled, rewardsEnabled]);

  // Redirects (contact portal fallback, or a non-canonical Teams URL -> /en-us/teams). location.replace keeps
  // the redirected URL out of history; nothing renders while it is in flight so there's no flash of content.
  const redirectUrl = decision.kind === 'redirect' ? decision.url : null;
  useEffect(() => {
    if (redirectUrl) {
      window.location.replace(redirectUrl);
    }
  }, [redirectUrl]);

  if (decision.kind === 'redirect') {
    return null;
  }
  const route = decision.route;

  const navigate = (target: string) => {
    if (target === 'teams') {
      window.open(`${window.location.origin}${TEAMS_CLEAN_PATH}`, '_blank', 'noopener,noreferrer');
      return;
    }
    window.location.hash = target === 'settings' ? '#/settings' : '#/';
  };

  if (route === 'teams') {
    return <TeamsSupportPage />;
  }

  if (route === 'rewards') {
    return <RewardsSupportPage locale={readRewardsLocale()} />;
  }

  if (route === 'admin') {
    return <AdminPortal />;
  }

  const renderMainContent = () => {
    if (route === 'settings') return <SettingsPage />;
    if (isSignedIn) {
      return <DashboardPage onNewCase={() => setChatRequest({ action: 'open' })} />;
    }
    return <LandingPage onAskAssistant={(msg) => setChatRequest({ action: 'send', message: msg })} onNavigate={navigate} />;
  };

  return (
    <div style={styles.shell}>
      <Header onNavigate={navigate} selectedAgent={selectedAgent} onAgentChange={changeAgent} />
      <main style={styles.main}>
        {renderMainContent()}
      </main>
      {/* Always render the built-in ChatWidget (the original, unchanged). When the
          CDN-widget flag is on, ADDITIONALLY mount the CDN-hosted generic widget
          in a separate dock so the two can be compared side by side. */}
      <ChatWidget
        chatRequest={chatRequest}
        onChatRequestConsumed={() => setChatRequest(null)}
        entryPointOverride={selectedAgent.entryPoint}
        agentNameOverride={selectedAgent.agentName}
      />
      {useCdnWidget && <CdnChatWidget entryPoint={selectedAgent.entryPoint} />}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  shell: {
    minHeight: '100vh',
    background: '#f9fafb',
    fontFamily: 'Segoe UI, system-ui, sans-serif',
  },
  main: { paddingBottom: 0 },
};
